import React, {useState} from 'react';
import { useMsal } from '@azure/msal-react';
import { RND_PLANNING_PROJECT, VIEW_ALL_PLANNED, VIEW_ACTIVE_AND_CURRENT } from '../Common/Constants';
import { AzureDevOpsService, ProjectWorkItemData } from '../services/AzureDevOpsService';
import { PublicClientApplication } from '@azure/msal-browser';
import { Ticket } from '../services/Ticket';
import TimelineAdo from '../Timeline/Timeline';
import ToolBarAdo, {OptionType} from '../Timeline/Toolbar/ToolBar';
import { createAssigneeList } from '../utils/PrepareTicket';
import { PrepareTimelineDataAdoSimplified } from '../Timeline/data/PrepareTimelineData';
import dayjs from 'dayjs';

// Simple loading spinner component
const LoadingSpinner: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 40 }}>
    <div style={{
      border: '8px solid #f3f3f3',
      borderTop: '8px solid #3498db',
      borderRadius: '50%',
      width: 60,
      height: 60,
      animation: 'spin 1s linear infinite',
    }} />
    <span style={{ marginTop: 16, fontSize: 18, color: '#555' }}>Loading...</span>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

interface RnDPlanningPageProps {
  useAdvancedMode: boolean;
  projectWorkItemData: ProjectWorkItemData[];
  usersData: any[];
  capexOptions: string[];
  govProjectOptions: string[];
  priorityDriverOptions: string[];
  demoOptions: string[];
  iterationsData: any[];
  productOptions: string[];
  investmentClassOptions: string[];
  selectedPriorityDriver: string;
  setSelectedPriorityDriver: (value: string) => void;
}

const RnDPlanningPage: React.FC<RnDPlanningPageProps> = (props ) => {
  const {instance, accounts } = useMsal();
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showChartMenu, setShowChartMenu] = useState(false);
  const [refresh, setRefresh] = useState<Date | null>()
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0);
  const [viewId, setViewId] = useState<string>(VIEW_ACTIVE_AND_CURRENT);
  const [quarterFilter, setQuarterFilter] = useState<any>(null);
  const [teamsId, setTeamsId] = useState<string>(localStorage.getItem("RnDPlanning-teamsId") || "");
  const [assigneeId, setAssigneeId] = useState<string>(localStorage.getItem("RnDPlanning-assigneeId") || "");
  const [includeChildren, setIncludeChildren] = useState<boolean>(localStorage.getItem("RnDPlanning-includeChildren") !== "false");
  const [selectedTags, setSelectedTags] = useState<string[]>(JSON.parse(localStorage.getItem("RnDPlanning-selectedTags") || "[]"));
  const [selectedAreaPath, setSelectedAreaPath] = useState<string>(localStorage.getItem("RnDPlanning-selectedAreaPath") || "");
  const [areaPathList, setAreaPathList] = useState<OptionType[]>([{ value: "", label: "All" }]);
  const [multiSelectMode, setMultiSelectMode] = useState<boolean>(false);
  const [selectedTicketsCount, setSelectedTicketsCount] = useState<number>(0);
  const [onEditMultiSelect, setOnEditMultiSelect] = useState<(() => void) | null>(null);

  React.useEffect(() => {
    if (!multiSelectMode) {
      setSelectedTicketsCount(0);
      setOnEditMultiSelect(null);
    }
  }, [multiSelectMode]);

  const devOpsService = React.useMemo(() => 
    accounts.length > 0 ? new AzureDevOpsService(instance as PublicClientApplication, accounts[0]) : null,
    [instance, accounts]
  );

  const wiql = "Select [System.Id] , [System.Title], [System.State], [Custom.EndIteration], [Custom.StartIteration] From WorkItems Where [Custom.EndIteration] <> '' AND [State] <> 'Closed' and [System.TeamProject] = @project order by [Custom.PlanningPriority] asc, [Custom.StartIteration] asc, [System.CreatedDate] asc";

  React.useEffect(() => {
    document.title = "R&D Planning";
    const fetchTickets = async () => {
      if (accounts.length > 0) {
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const project = RND_PLANNING_PROJECT;
          const result = await devOpsService.fetchTicketsByQuery(organization, project, wiql);

          if (result && Array.isArray(result.workItems) && result.workItems.length > 0) {
            // Parallelize fetching ticket details
            const ticketDetails: Ticket[] = await Promise.all(
              result.workItems.map(async (item: any) => {
                if (item.url) {
                  try {
                    const detail = await devOpsService.fetchWorkItemByUrl(item.url);
                    if (detail && detail.id) {
                      // TODO: Add all children
                      const _ticket : Ticket = new Ticket(detail);
                      _ticket.children = [];


                      if (_ticket.childrenIds && _ticket.childrenIds.length > 0) {
                        const childrenDetails = await Promise.all(
                          _ticket.childrenIds.map(async (childId: number) => {
                            try {
                              const resultChild = await devOpsService.fetchWorkItemById(childId);
                              const _ticketChild = new Ticket(resultChild, _ticket.initiative);
                              return _ticketChild;
                            } catch (err) {
                              console.error("Error fetching child ticket:", err);
                              return null;
                            }
                          })
                        );
                        _ticket.children = childrenDetails.filter((child): child is Ticket => child !== null);
                      }

                      return _ticket;
                    }
                  } catch (err) {
                    console.error("Error fetching:", err);
                  }
                }
                return null;
              })
            );
            // Filter out any nulls (failed fetches)
            const allTickets = ticketDetails.filter((t): t is Ticket => t !== null);

            // Add all children to the ticketDetails array
            const allChildren : Ticket[] = allTickets.flatMap(ticket => ticket.children || []);
            
            // Filter children based on includeChildren setting
            const filteredChildren = includeChildren 
              ? allChildren 
              : allChildren.filter(child => child.project === RND_PLANNING_PROJECT);
            
            const uniqueTickets : Ticket[] = [...allTickets, ...filteredChildren].reduce((acc, ticket) => {
              if (!acc.some((t: Ticket) => t.id === ticket.id)) {
              acc.push(ticket);
              }
              return acc;
            }, [] as Ticket[]);
            setTickets(uniqueTickets.filter((t): t is Ticket => t !== null));
            
            setIsLoaded(true);
            setIsRefreshing(false);
          } else {
            setTickets([]);
            setIsLoaded(true);
            setIsRefreshing(false);
          }
        } catch (error) {
          console.error("Error fetching tickets:", error);
          setTickets([]);
          setIsLoaded(true);
          setIsRefreshing(false);
        }
      }
    };
    fetchTickets();
  }, [instance, accounts, refresh, setTickets, includeChildren]);

  const convertLegacyQuarterToDate = (quarterString: string): any => {
    const match = quarterString.match(/^(\d{2})Q(\d)$/);
    if (!match) return null;
    
    const year = 2000 + parseInt(match[1]);
    const quarter = parseInt(match[2]);
    
    const month = (quarter - 1) * 3;
    
    return dayjs().year(year).month(month).startOf('quarter' as any);
  };

  const getFilteredAssigneeList = () => {
    if (!tickets) return [];
    
    const timelineData = PrepareTimelineDataAdoSimplified(
      tickets, 
      teamsId, 
      props.selectedPriorityDriver, 
      "", // Empty assigneeId to get all assignees
      viewId, 
      quarterFilter
    );
    
    return createAssigneeList(timelineData.filteredTickets);
  };
  
  const assigneeList = getFilteredAssigneeList();

  const getFilteredTagList = (): OptionType[] => {
    if (!tickets) return [];
    
    const timelineData = PrepareTimelineDataAdoSimplified(
      tickets,
      teamsId,
      props.selectedPriorityDriver,
      assigneeId,
      viewId,
      quarterFilter,
      []
    );
    
    const allTags = new Set<string>();
    timelineData.filteredTickets.forEach(ticket => {
      ticket.tagList?.forEach(tag => allTags.add(tag));
    });
    
    return Array.from(allTags).sort().map(tag => ({ value: tag, label: tag }));
  };

  const tagList = getFilteredTagList();

  // Fetch area paths from Azure DevOps and cache them
  React.useEffect(() => {
    const fetchAreaPaths = async () => {
      if (!devOpsService) return;
      
      // Check cache first
      const cachedAreaPaths = localStorage.getItem("RnDPlanning-areaPathsCache");
      const cacheTimestamp = localStorage.getItem("RnDPlanning-areaPathsCacheTime");
      const cacheExpiryMinutes = 15; // Cache for 15 minutes
      
      if (cachedAreaPaths && cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp);
        if (cacheAge < cacheExpiryMinutes * 60 * 1000) {
          // Use cached data
          setAreaPathList(JSON.parse(cachedAreaPaths));
          return;
        }
      }
      
      // Fetch from Azure DevOps
      try {
        const organization = 'sensio';
        const project = RND_PLANNING_PROJECT;
        const areaPathsData = await devOpsService.fetchAreaPaths(organization, project);
        
        if (areaPathsData && areaPathsData.children) {
          const paths: OptionType[] = [
            { value: "", label: "All" },
            { value: "__ROOT__", label: "Root" }
          ];
          
          // Extract first-level area paths
          areaPathsData.children.forEach((child: any) => {
            if (child.name) {
              paths.push({ value: child.name, label: child.name });
            }
          });
          
          paths.sort((a, b) => {
            if (a.value === "") return -1;
            if (b.value === "__ROOT__") return 1;
            return a.label.localeCompare(b.label);
          });
          
          setAreaPathList(paths);
          
          // Clear selected area path if it's no longer available
          if (selectedAreaPath && !paths.some(item => item.value === selectedAreaPath)) {
            setSelectedAreaPath("");
          }
          
          // Cache the results
          localStorage.setItem("RnDPlanning-areaPathsCache", JSON.stringify(paths));
          localStorage.setItem("RnDPlanning-areaPathsCacheTime", Date.now().toString());
        }
      } catch (error) {
        console.error("Error fetching area paths:", error);
      }
    };
    
    fetchAreaPaths();
  }, [devOpsService, selectedAreaPath]);

  React.useEffect(() => {
    const storedViewId = localStorage.getItem("RnDPlanning-viewId");
    const storedQuarterFilter = localStorage.getItem("RnDPlanning-quarterFilter");
    if (storedViewId) {
      setViewId(storedViewId);
    }

    if (storedQuarterFilter && storedQuarterFilter !== "All" && storedQuarterFilter !== "") {
      const parsedDate = dayjs(storedQuarterFilter);
      if (parsedDate.isValid()) {
        setQuarterFilter(parsedDate);
      } else {
        const legacyQuarter = convertLegacyQuarterToDate(storedQuarterFilter);
        if (legacyQuarter) {
          setQuarterFilter(legacyQuarter);
        }
      }
    }
  }, []);

  React.useEffect(() => {
    try {
      localStorage.setItem("RnDPlanning-viewId", viewId);
    } catch (storageError) {
      console.warn('Failed to save RnDPlanning-viewId due to storage quota:', storageError);
    }
  }, [viewId]);

  React.useEffect(() => {
    try {
      localStorage.setItem("RnDPlanning-quarterFilter", quarterFilter ? quarterFilter.toISOString() : "");
    } catch (storageError) {
      console.warn('Failed to save RnDPlanning-quarterFilter due to storage quota:', storageError);
    }
  }, [quarterFilter]);

  React.useEffect(() => {
    try {
      localStorage.setItem("RnDPlanning-includeChildren", includeChildren.toString());
    } catch (storageError) {
      console.warn('Failed to save RnDPlanning-includeChildren due to storage quota:', storageError);
    }
  }, [includeChildren]);

  React.useEffect(() => {
    try {
      localStorage.setItem("RnDPlanning-selectedTags", JSON.stringify(selectedTags));
    } catch (storageError) {
      console.warn('Failed to save RnDPlanning-selectedTags due to storage quota:', storageError);
    }
  }, [selectedTags]);

  React.useEffect(() => {
    try {
      localStorage.setItem("RnDPlanning-selectedAreaPath", selectedAreaPath);
    } catch (storageError) {
      console.warn('Failed to save RnDPlanning-selectedAreaPath due to storage quota:', storageError);
    }
  }, [selectedAreaPath]);

  if (isLoaded) {

    
    return (
      <>
      {isRefreshing && (
        <div style={{ 
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          zIndex: 9999,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <LoadingSpinner />
          <div style={{ marginTop: '10px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
            Refreshing children filter...
          </div>
        </div>
      )}
      <ToolBarAdo
        // Data
        projects={[]}
        initiativeList={[{label:"hei", value: "hoi"}]}
        assigneeList={assigneeList}
        priorityDriverOptions={props.priorityDriverOptions}
        tagList={tagList}
        areaPathList={areaPathList}

        // Filters
        teamsId={teamsId}
        quarterFilter={quarterFilter}
        viewId={viewId}
        selectedProject={RND_PLANNING_PROJECT}
        selectedInitiative={""}
        assigneeId={assigneeId}
        selectedPriorityDriver={props.selectedPriorityDriver}
        selectedTags={selectedTags}
        selectedAreaPath={selectedAreaPath}
        filterMinPlanningPri={-999999999}
        filterMaxPlanningPri={9999999999}
        includeChildren={includeChildren}
        // Filter callbacks
        setFilterMinPlanningPri= {(min: number) => {return}}
        setFilterMaxPlanningPri= {(max: number) => {return}}
        setQuarterFilter={setQuarterFilter}
        setTeamsId={(value:string) => {
          setTeamsId (value); 
          try {
            localStorage.setItem("RnDPlanning-teamsId", value);
          } catch (storageError) {
            console.warn('Failed to save RnDPlanning-teamsId due to storage quota:', storageError);
          }
        }}
        setSelectedPriorityDriver={props.setSelectedPriorityDriver}
        setSelectedTags={(values: string[]) => {
          setSelectedTags(values);
          try {
            localStorage.setItem("RnDPlanning-selectedTags", JSON.stringify(values));
          } catch (storageError) {
            console.warn('Failed to save RnDPlanning-selectedTags due to storage quota:', storageError);
          }
        }}
        setSelectedAreaPath={(value: string) => {
          setSelectedAreaPath(value);
          try {
            localStorage.setItem("RnDPlanning-selectedAreaPath", value);
          } catch (storageError) {
            console.warn('Failed to save RnDPlanning-selectedAreaPath due to storage quota:', storageError);
          }
        }}
        setIncludeChildren={(value: boolean) => {
          setIncludeChildren(value);
          try {
            localStorage.setItem("RnDPlanning-includeChildren", value.toString());
          } catch (storageError) {
            console.warn('Failed to save RnDPlanning-includeChildren due to storage quota:', storageError);
          }
          setIsRefreshing(true);
          const d = new Date();
          setRefresh(d);
        }}
        // Chart visuals
        chartHeight={1300}
        onHandleChartHeightChange={(value: number | null) => {return}}
        setOpenOnClickStatus= {(status: boolean) => {return}}
        openOnClickStatus={true}
        showTeamsFilter={true}       
        useAdvancedMode={props.useAdvancedMode} // Pass useAdvanced  
        multiSelectMode={multiSelectMode}
        onMultiSelectModeChange={setMultiSelectMode}
        selectedTicketsCount={selectedTicketsCount}
        onEditMultiSelect={() => onEditMultiSelect && onEditMultiSelect()}

        // Callbacks
        projectOnChange={(option: OptionType | null) => {return}}
        onHideProject= {(project: string) => {return}}
        initiaitveOnChange={(option: OptionType | null) => {return}}
        viewOnChange={(option: OptionType | null) => {
          if (option) {
            setViewId(option.value);
          }
        }}
        assigneeOnChange={(option: OptionType | null) => {
          const value = option ? option.value : "";
          setAssigneeId(value);
          try {
            localStorage.setItem("RnDPlanning-assigneeId", value);
          } catch (storageError) {
            console.warn('Failed to save RnDPlanning-assigneeId due to storage quota:', storageError);
          }
        }}
        onRefreshClick= {() => {
          const now = Date.now();
          const timeSinceLastRefresh = now - lastRefreshTime;
          
          // If refresh clicked within 2 seconds, clear all caches
          if (timeSinceLastRefresh < 2000) {
            console.log("Double refresh detected - clearing all caches");
            // Clear area path cache
            localStorage.removeItem("RnDPlanning-areaPathsCache");
            localStorage.removeItem("RnDPlanning-areaPathsCacheTime");
            // Clear AuthenticatedApp caches
            localStorage.removeItem("workItemData");
            localStorage.removeItem("workItemDataTimestamp");
            localStorage.removeItem("usersData");
            localStorage.removeItem("usersDataTimestamp");
            localStorage.removeItem("capexData");
            localStorage.removeItem("capexDataTimestamp");
            localStorage.removeItem("govProjectData");
            localStorage.removeItem("govProjectDataTimestamp");
            localStorage.removeItem("priorityDriverData");
            localStorage.removeItem("priorityDriverDataTimestamp");
            localStorage.removeItem("demoData");
            localStorage.removeItem("demoDataTimestamp");
            localStorage.removeItem("initiativeData");
            localStorage.removeItem("initiativeDataTimestamp");
            localStorage.removeItem("productData");
            localStorage.removeItem("productDataTimestamp");
            localStorage.removeItem("investmentClassData");
            localStorage.removeItem("investmentClassDataTimestamp");
            localStorage.removeItem("iterationsData");
            localStorage.removeItem("iterationsDataTimestamp");
            window.location.reload();
          } else {
            const d : Date = new Date() 
            setRefresh(d);
            setLastRefreshTime(now);
          }
        }}
        onLogout= {() => {return}}
      />
        <TimelineAdo
          items={tickets || []}
          showChartMenu={showChartMenu}
          setShowChartMenu={setShowChartMenu}
          viewId={viewId || VIEW_ALL_PLANNED}
          quarterFilter={quarterFilter}
          selectedPriorityDriver={props.selectedPriorityDriver || ""}
          selectedTags={selectedTags}
          selectedAreaPath={selectedAreaPath || ""}
          //Todo improve the project name logic here
          project={RND_PLANNING_PROJECT}
          teamId={teamsId || ""}
          assigneeId={assigneeId || ""}
          showTeams={true}
          useAdvancedMode={props.useAdvancedMode} // Pass useAdvancedMode prop
          projectWorkItemData={props.projectWorkItemData}
          usersData={props.usersData}
          capexOptions={props.capexOptions}
          govProjectOptions={props.govProjectOptions}
          priorityDriverOptions={props.priorityDriverOptions}
          demoOptions={props.demoOptions}
          productOptions={props.productOptions}
          investmentClassOptions={props.investmentClassOptions}
          areaPathOptions={areaPathList.map(item => item.value).filter(value => value !== "" && value !== "__ROOT__")}
          azureDevOpsService={devOpsService}
          iterationsData={props.iterationsData}
          multiSelectMode={multiSelectMode}
          onMultiSelectChange={(count, editHandler) => {
            setSelectedTicketsCount(count);
            setOnEditMultiSelect(() => editHandler);
          }}
          onSetRefresh={() => {
            const d: Date = new Date();
            setRefresh(d);
          } }
          onUpdateTicket={async (ticketId: number, updatedFields: Record<string, any>) => {
            if (devOpsService) {
              await devOpsService.updateTicket(ticketId, updatedFields);
            }
          } }
          onMoveAndExtendIteration={async (selectedTicket: number, move: number, extend: number) => {
            if (devOpsService) {
              await devOpsService.moveAndExtendTicketIteration(selectedTicket, move, extend);
            }
          } }
          RemoveFromTimeline={async (ticketId: number) => {
            if (devOpsService) {
              await devOpsService.removeTicketFromTimeline(ticketId);
            }
          } }

          AddPlanningTask={async (selectedTicket: Ticket, spec: string) => {
            if (devOpsService) {
              await devOpsService.addPlanningTask(selectedTicket, spec, RND_PLANNING_PROJECT, "Initiative");
            }
          } } 
          createTicket={function (project: string, ticketType: string, fields: Record<string, any>, parentUrl : string): Promise<any> {
            if (devOpsService) {
              return devOpsService.createTicket(project, ticketType, fields, parentUrl);
            }
            return Promise.reject(new Error('DevOps service not available'));
          } }        
          FindTicket={async (ticketId: string) => {
            if (devOpsService) {
              return await devOpsService.fetchWorkItemById(Number(ticketId));
            }
            return null;
          } }
          AddTicketToSprint={async (ticketId: string, startDate: string, endDate: string) => {
            if (devOpsService) {
              return await devOpsService.addTicketToSprint(ticketId, startDate, endDate);
            }
            return null;
          } }
        />

      </>
    );
  } else {
    return <LoadingSpinner />;
  }
};
export default RnDPlanningPage;
